import { ContestParticipants } from "./contest/ContestParticipants";
import { ContestActivation } from './contest/ContestActivation';
import React from 'react';
import {
    Create,
    Edit,
    TabbedForm,
    FormTab,
    SelectInput,
    FormDataConsumer,
    EditButton,
    SimpleForm,
    ReferenceInput,
    AutocompleteInput,
    TextInput,
    BooleanInput,
    ArrayInput,
    SimpleFormIterator,
    NumberInput,
    required,
    Datagrid,
    FunctionField,
    List,
    TextField,
    Toolbar,
    SaveButton,
    useNotify,
} from 'react-admin';

import AdminPagination from '../AdminPagination';

export const ContestAdminList = (props: any) => (
    <List
        {...props}
        title="Jeux concours"
        perPage={25}
        pagination={<AdminPagination />}
        bulkActionButtons={false}
        sort={{ field: 'createdAt', order: 'DESC' }}
    >
        <Datagrid>
            <EditButton />
            <TextField source="event_name" label="Événement" />
            <TextField source="owner_email" label="E-mail organisateur" />
            <FunctionField
                label="État"
                render={(record: any) =>
                    record.enabled ? 'Activé' : 'Désactivé'
                }
            />
            <FunctionField
                label="Création"
                render={(record: any) =>
                    record.createdAt
                        ? new Date(record.createdAt).toLocaleDateString('fr-FR')
                        : '—'
                }
            />
        </Datagrid>
    </List>
);

export const ContestAdminCreate = (props: any) => (
    <Create {...props} transform={(data: any) => ({
        ...data,
        texts: {},
        partners: [],
        prizes: [],
        background: 'tropical-02',
        enabled: Boolean(data.enabled),
    })}>
        <SimpleForm redirect="list" initialValues={{enabled: false}}>
            <ReferenceInput source="owner_id" reference="user" label="Organisateur" validate={required()} perPage={1000}>
                <AutocompleteInput optionText="email" />
            </ReferenceInput>
            <TextInput source="name" label="Nom du jeu concours" validate={required()} fullWidth />
            <BooleanInput source="enabled" label="Concours actif" />
        </SimpleForm>
    </Create>
);


const ContestSaveToolbar = (props: any) => (
    <Toolbar {...props}>
        <SaveButton
            label={props.saving ? 'Enregistrement en cours…' : 'Enregistrer'}
            disabled={props.saving}
        />
    </Toolbar>
);

const ContestSaveNotifications = () => {
    const notify = useNotify();
    return {
        onSuccess: () => notify('Modifications enregistrées ✓', 'info'),
        onFailure: () => notify("Échec de l'enregistrement. Vérifiez les champs.", 'warning'),
    };
};

export const ContestAdminEdit = (props: any) => {
    const notifications = ContestSaveNotifications();
    return (
    <Edit {...props} title="Personnaliser le jeu concours"
        mutationMode="pessimistic"
        onSuccess={notifications.onSuccess}
        onFailure={notifications.onFailure}
        transform={(data: any) => {
            const stamp = Date.now().toString(36);
            const partners = (data.partners || []).map((p: any, i: number) => ({
                ...p,
                id: p.id || `partenaire-${stamp}-${i}`,
                logo: p.logo?.trim() || undefined,
                enabled: Boolean(p.enabled),
            }));
            const prizes = (data.prizes || []).map((g: any, i: number) => {
                const match = /^__nouveau_(\d+)$/.exec(g.partner_id || "");
                return {
                    ...g,
                    id: g.id || `cadeau-${stamp}-${i}`,
                    custom_image: g.custom_image?.trim() || undefined,
                    partner_id: match
                        ? partners[Number(match[1])]?.id
                        : (g.partner_id || undefined),
                    description: g.description || "",
                    quantity: Number(g.quantity ?? 0),
                    enabled: Boolean(g.enabled),
                };
            });
            return {
                ...data,
                texts: data.texts || {},
                partners,
                prizes,
                background: "tropical-02",
                enabled: Boolean(data.enabled),
                require_email: false,
            };
        }}>
        <TabbedForm redirect={false} toolbar={<ContestSaveToolbar />} className="contest-mobile-tabs">
            <FormTab label="Général">
            <ReferenceInput source="owner_id" reference="user" label="Organisateur" disabled>
                <AutocompleteInput optionText="email" disabled />
            </ReferenceInput>
            <TextInput source="name" label="Nom du jeu concours" validate={required()} fullWidth />
            <BooleanInput source="enabled" label="Configuration active" />

            </FormTab>
            <FormTab label="Textes">
            <TextInput source="texts.title" label="Titre principal" fullWidth />
            <TextInput source="texts.introduction" label="Introduction" multiline fullWidth />
            <TextInput source="texts.partners_title" label="Titre des partenaires" fullWidth />
            <TextInput source="texts.prizes_title" label="Titre des cadeaux" fullWidth />
            <TextInput source="texts.form_title" label="Titre du formulaire" fullWidth />
            <TextInput source="texts.firstname_label" label="Libellé prénom ou surnom" fullWidth />
            <TextInput source="texts.phone_label" label="Libellé téléphone" fullWidth />
            <TextInput source="texts.email_label" label="Libellé email facultatif" fullWidth />
            <TextInput source="texts.phone_notice" label="Information sur les SMS" multiline fullWidth />
            <TextInput source="texts.consent_label" label="Texte de consentement" multiline fullWidth />
            <TextInput source="texts.submit_label" label="Texte du bouton de participation" fullWidth />
            <TextInput source="texts.success_title" label="Titre de confirmation" fullWidth />
            <TextInput source="texts.success_message" label="Message de confirmation" multiline fullWidth />
            <TextInput source="texts.closed_message" label="Message inscriptions fermées" multiline fullWidth />

            </FormTab>
            <FormTab label="Partenaires">
            <ArrayInput source="partners" label="Liste des partenaires">
                <SimpleFormIterator>
                    <TextInput source="name" label="Nom du partenaire" validate={required()} />
                    <TextInput source="description" label="Présentation" multiline />
                    <TextInput source="logo" label="URL HTTPS du logo" />
                    <ArrayInput source="links" label="Liens du partenaire">
                        <SimpleFormIterator>
                            <TextInput source="label" label="Texte du lien" validate={required()} />
                            <TextInput source="url" label="URL HTTPS" validate={required()} />
                        </SimpleFormIterator>
                    </ArrayInput>
                    <BooleanInput source="enabled" label="Afficher le partenaire" />
                </SimpleFormIterator>
            </ArrayInput>

            </FormTab>
            <FormTab label="Cadeaux">
            <ArrayInput source="prizes" label="Liste des cadeaux">
                <SimpleFormIterator>
                    <TextInput source="name" label="Nom du cadeau" validate={required()} />
                    <TextInput source="description" label="Description" multiline />
                    <SelectInput source="illustration" label="Illustration du cadeau" validate={required()} choices={[
                        {id:"cadeau",name:"Cadeau surprise"},
                        {id:"voyage",name:"Voyage"},
                        {id:"hotel",name:"Séjour / Hôtel"},
                        {id:"restaurant",name:"Restaurant"},
                        {id:"beaute",name:"Beauté / Bien-être"},
                        {id:"mode",name:"Mode"},
                        {id:"musique",name:"Musique"},
                        {id:"hightech",name:"High-tech"},
                        {id:"maison",name:"Maison"},
                        {id:"loisirs",name:"Loisirs"},
                        {id:"panier",name:"Panier gourmand"},
                        {id:"billet",name:"Billets / Entrées"}
                    ]} />
                    <TextInput source="custom_image" label="URL HTTPS image personnalisée" />
                    <FormDataConsumer>
                        {({ formData, getSource }: any) => (
                            <SelectInput
                                source={getSource("partner_id")}
                                label="Partenaire associé"
                                choices={(formData.partners || [])
                                    .filter((x: any) => x.name)
                                    .map((x: any, i: number) => ({
                                        id: x.id || `__nouveau_${i}`,
                                        name: x.name
                                    }))}
                                emptyText="Aucun partenaire"
                            />
                        )}
                    </FormDataConsumer>
                    <NumberInput source="quantity" label="Quantité" min={0} max={100000} validate={required()} />
                    <BooleanInput source="enabled" label="Afficher le cadeau" />
                </SimpleFormIterator>
            </ArrayInput>
        </FormTab>
            <FormTab label="Activation & QR code">
                <FormDataConsumer>
                    {({ formData }: any) => (
                        <ContestActivation
                            contestId={String(formData.id || formData._id || '')}
                        />
                    )}
                </FormDataConsumer>
            </FormTab>
            <FormTab label="Participants">
                <FormDataConsumer>
                    {({ formData }: any) => (
                        <ContestParticipants
                            contestId={String(formData.id || formData._id || "")}
                        />
                    )}
                </FormDataConsumer>
            </FormTab>
        </TabbedForm>
    </Edit>
    );
};
